/**
 * PersonalLinkBlock
 * The "Personlig länk" block at the top of the assignment composer, always
 * expanded so the admin decides with the facts in view: one link per
 * external user opens every task delegated to them, the tasks it opens
 * today (count and titles), and the link's validity — which, for an
 * existing link, applies to all of those tasks.
 *
 * The intro follows the link's state: none, active, expired (replaced by a
 * new link on delegation) or revoked (a new link is issued). Expiry ends
 * access, so the block says what the end date means, why it was suggested
 * (latest due date + 14 days) or that it extends the active link. Validity
 * state lives in useLinkValidityChoice.
 */
import React from 'react';
import { Button } from 'react-bootstrap';
import { ExternalLinkInfo } from '../api/externalLinksApi';
import { LinkValidityChoice } from '../hooks/useLinkValidityChoice';
import { GuestTaskType } from '../types/guestAccess';
import { formatDateForDisplay } from '../Helpers/DateUtils';
import { expiryWarning, lastRevokedAt, SUGGESTION_MARGIN_DAYS, swedishGenitive } from '../utils/linkValidity';
import LinkTaskList from './LinkTaskList';
import LinkValidityControl from './LinkValidityControl';

interface PersonalLinkBlockProps {
    /** The recipient's full name */
    recipientName: string;
    taskType: GuestTaskType;
    /** The task being delegated; left out of the list (it is added below it) */
    taskKey?: string;
    /** The recipient's link, log and tasks (useExternalLink) */
    info: ExternalLinkInfo | null;
    choice: LinkValidityChoice;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
    disabled?: boolean;
    idSuffix: string;
}

/** The intro sentence for the link's state. */
export function personalLinkIntro(
    firstName: string,
    taskType: GuestTaskType,
    info: ExternalLinkInfo | null
): string {
    const all = `alla uppgifter som delegeras till ${firstName}`;
    const state = info?.link?.state;
    if (state === 'active') {
        const thisTask = taskType === 'project' ? 'Det här projektet' : 'Det här ärendet';
        return `${firstName} har redan en personlig länk som visar ${all}. ${thisTask} läggs till, och e-posten innehåller samma länk.`;
    }
    if (state === 'expired') {
        return `${swedishGenitive(firstName)} länk har gått ut och ger inte längre åtkomst. Delegeringen skapar en ny länk som visar ${all}.`;
    }
    const revokedAt = info ? lastRevokedAt(info.log) : undefined;
    if (revokedAt) {
        return `${swedishGenitive(firstName)} länk återkallades ${formatDateForDisplay(revokedAt)}. Delegeringen skapar en ny länk som visar ${all}.`;
    }
    return `${firstName} får en personlig länk som visar ${all}, det här och kommande. Samma länk står i varje e-post.`;
}

/** Why the value is what it is, and what the end date means. */
const ValidityHint: React.FC<{ firstName: string; choice: LinkValidityChoice }> = ({ firstName, choice }) => {
    const { value, suggestion, extendsFrom, existing } = choice;
    const lines: string[] = [];
    if (extendsFrom && suggestion) {
        lines.push(`Förlängs från ${formatDateForDisplay(extendsFrom)}: ${suggestion.basis.title} förfaller ${formatDateForDisplay(suggestion.basis.dueDate)}.`);
    } else if (!existing && suggestion && value?.kind === 'expiring' && value.expiresAt === suggestion.expiresAt) {
        lines.push(`Föreslaget: senaste förfallodatum (${suggestion.basis.title}, ${formatDateForDisplay(suggestion.basis.dueDate)}) + ${SUGGESTION_MARGIN_DAYS} dagar.`);
    }
    if (!value) {
        return <div className="small text-danger mt-1">Ingen uppgift har ett förfallodatum. Välj hur länge länken ska gälla.</div>;
    }
    if (value.kind === 'expiring' && value.expiresAt) {
        lines.push(`Efter ${formatDateForDisplay(value.expiresAt)} har ${firstName} ingen åtkomst till uppgifterna.`);
    }
    return lines.length > 0 ? <div className="small text-muted mt-1">{lines.join(' ')}</div> : null;
};

const PersonalLinkBlock: React.FC<PersonalLinkBlockProps> = ({
    recipientName,
    taskType,
    taskKey,
    info,
    choice,
    loading,
    error,
    onRetry,
    disabled,
    idSuffix,
}) => {
    const firstName = recipientName.trim().split(/\s+/)[0] || recipientName;
    const active = info?.link?.state === 'active';
    const others = (info?.tasks ?? []).filter((t) => t.key !== taskKey);
    const thisTask = taskType === 'project' ? 'det här projektet' : 'det här ärendet';

    return (
        <div className="border rounded p-2 mb-3 d-flex flex-column gap-2">
            <div className="fw-semibold">{`Personlig länk till ${recipientName}`}</div>
            {loading ? (
                <div className="small text-muted">Hämtar länken…</div>
            ) : error ? (
                <div className="small">
                    <span className="text-danger">{error}</span>
                    {onRetry && (
                        <Button variant="link" size="sm" className="p-0 ms-2" disabled={disabled} onClick={onRetry}>
                            Försök igen
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    <div className="small">{personalLinkIntro(firstName, taskType, info)}</div>
                    {active && info?.link && (
                        <div className="small">
                            {info.link.kind === 'permanent'
                                ? 'Länken är permanent.'
                                : `Länken gäller till ${formatDateForDisplay(info.link.expiresAt)}${expiryWarning(info.link) ? ` – ${expiryWarning(info.link)}` : ''}.`}
                        </div>
                    )}
                    <LinkTaskList
                        tasks={others}
                        heading={active ? 'Via länken idag' : 'Länken kommer också att visa'}
                        emptyText={active ? 'Inga andra öppna uppgifter via länken idag.' : undefined}
                    />
                    {(active || others.some((t) => !t.completedAt)) && (
                        <div className="small">{`+ ${thisTask}`}</div>
                    )}
                    <div>
                        <div className="small fw-semibold mb-1">
                            {active ? `Giltighet – gäller länken, alltså alla ${swedishGenitive(firstName)} uppgifter:` : 'Giltighet för länken:'}
                        </div>
                        <LinkValidityControl
                            value={choice.value}
                            onChange={choice.setValue}
                            disabled={disabled}
                            idSuffix={idSuffix}
                            expiringDefault={choice.suggestion?.expiresAt ?? choice.existing?.expiresAt}
                        />
                        <ValidityHint firstName={firstName} choice={choice} />
                    </div>
                </>
            )}
        </div>
    );
};

export default PersonalLinkBlock;
